/**
 * The four backgrounds a new character is made from (M7-03).
 *
 * Ninety seconds of creation, and this is the half of it that has consequences:
 * a background is a pre-loaded role — a kit, an attribute lean, and one line of
 * history — so that a first-time player picks *who they are* rather than filling
 * in a point-buy spreadsheet. The name is the other half, and it is free text.
 *
 * The design constraint is the load-bearing one: all four are combat-viable and
 * none is combat-optimal alone. The Milwr is the durable one and the Llysieuwr
 * the fragile one, but the gap between them is a matter of a few dice, not of a
 * fighter and a bystander — because the answer to "how do I win a fight" is
 * meant to be *the party*, not the sheet. See the milestone ticket.
 *
 * What a "familiarity" is, honestly. The engine has one skill scalar today —
 * `Loadout.skill`, "one scalar until skill families have a home" — and no
 * per-domain proficiency. So a familiarity is expressed with the tools the sim
 * actually has: the weapon and armour you start holding, an attribute lean (a
 * Meddyg's apprentice has the *wit* medicine reads from), and that one skill
 * number. When skill families land, this file is where a `tracking` or `forage`
 * proficiency attaches — the data shape is already a role, not a stat block.
 *
 * The sling is not here, and its absence is deliberate. `weapons.ts` defers the
 * sling whole ("a six-tile arc is a ranged combat system, not a row of data"),
 * so the Bugail fights with the staff that is actually their weapon; the sling
 * is the thing over their shoulder in the history line, not a row in the kit.
 */
import { createAttributes } from '@annwn/sim';
/**
 * Heliwr — the hunter. Spear and knife, and an eye for sign.
 *
 * The reach fighter of the four: a spear keeps the wolf at two tiles, which is
 * the whole of why an ordinary person survives the thing in the wood. Agile and
 * observant (the attributes a later `tracking` familiarity will read), lightly
 * held together — a hunter's armour is distance, not leather.
 */
export const HELIWR = {
    id: 'heliwr',
    name: 'Heliwr',
    familiarity: 'tracking',
    blurb: 'A hunter, at home in the wood and quick to read a track.',
    history: 'You have followed deer since you could walk, and you know the valley by its ' +
        'signs before its paths. The spear is your father’s; the eye is your own.',
    loadout: {
        planId: 'human',
        weaponId: 'spear',
        armourIds: ['tunic'],
        attributes: createAttributes({ agility: 12, frame: 11, wit: 11, toughness: 10, presence: 9 }),
        skill: 1,
    },
    // The knife of the blurb is a sidearm, not cargo; the satchel holds no second
    // weapon, so it lives in the history rather than as an item id that isn't one.
    // A scrap of tinder is the whole of the kit — a hunter sleeps out, and a fire
    // is the first thing the wood asks of them.
    carried: [{ itemId: 'tinder', count: 1 }],
};
/**
 * Milwr — the soldier. A seax behind a shield, and the training to use both.
 *
 * The durable one, and the closest the four come to a fighter — deliberately no
 * closer. A seax is "useless against a well-made byrnie", and the shield is what
 * a Milwr actually brings: they last, they hold a line, they are not the one who
 * kills Ysgithyrwyn on their own. Skill 2 is a war-band's training, the same
 * number the harness's spearman carries.
 */
export const MILWR = {
    id: 'milwr',
    name: 'Milwr',
    familiarity: 'the shield-wall',
    blurb: 'A soldier of some lord’s teulu, seax and shield and a season of war.',
    history: 'You stood in a shield-wall for a summer and came home when the hosting broke ' +
        'up. You know how to hold a line. Holding a life together is the newer trade.',
    loadout: {
        planId: 'human',
        weaponId: 'seax',
        armourIds: ['tunic', 'shield'],
        attributes: createAttributes({ frame: 12, toughness: 12, agility: 11, presence: 10, wit: 9 }),
        skill: 2,
    },
    // A soldier travels light and lit — a scrap of tinder and the seax, and the
    // rest is discipline.
    carried: [{ itemId: 'tinder', count: 1 }],
};
/**
 * Llysieuwr / Llysieuwraig — the herbalist. A knife, a satchel of herbs, and the
 * wit that medicine is rolled against (M4).
 *
 * The fragile one on purpose: a knife, no armour, the lightest frame. What they
 * bring is not their own survival but everyone else's — yarrow and sphagnum in
 * the satchel from minute one, and the wit to bind a wound before it turns. The
 * clearest statement of the design's thesis: the weakest fighter is the reason
 * the party lives, which is why the party is the build.
 */
export const LLYSIEUWR = {
    id: 'herbalist',
    name: 'Llysieuwr',
    nameFeminine: 'Llysieuwraig',
    familiarity: 'medicine',
    blurb: 'A herb-worker, satchel already full, the Meddyg’s reluctant equal.',
    history: 'You learned the plants at a grandmother’s knee and the binding of wounds at ' +
        'the Meddyg’s. You carry what the wood gives freely, against the day it does not.',
    loadout: {
        planId: 'human',
        weaponId: 'knife',
        armourIds: ['tunic'],
        attributes: createAttributes({ wit: 13, presence: 11, toughness: 10, agility: 10, frame: 9 }),
        skill: 0,
    },
    carried: [
        { itemId: 'yarrow', count: 3 },
        { itemId: 'sphagnum', count: 2 },
        { itemId: 'linen', count: 2 },
        { itemId: 'tinder', count: 1 },
    ],
};
/**
 * Bugail — the shepherd. A staff, a full belly, and a nose for what the ground
 * will give.
 *
 * The tough, self-sufficient one. The staff is "the poor man's spear" — it keeps
 * its distance and turns a blow it cannot avoid, a defender's weapon for the
 * person least likely to have chosen the fight. Toughness is the lean, forage the
 * familiarity: a Bugail is never quite hungry, which on the winter clock the
 * whole valley runs on is worth more than a sharper blade.
 */
export const BUGAIL = {
    id: 'bugail',
    name: 'Bugail',
    familiarity: 'foraging',
    blurb: 'A shepherd off the high ffridd, staff in hand and sling on the shoulder.',
    history: 'You have wintered alone with the flock above the treeline and come down thinner ' +
        'but never starving. You know which slope holds berries in a hard year.',
    loadout: {
        planId: 'human',
        weaponId: 'staff',
        armourIds: ['tunic'],
        attributes: createAttributes({ toughness: 12, frame: 11, agility: 10, wit: 10, presence: 9 }),
        skill: 1,
    },
    carried: [
        { itemId: 'hazelnuts', count: 2 },
        { itemId: 'tinder', count: 1 },
    ],
};
/** The four, in the order the creation screen shows them. */
export const BACKGROUNDS = [HELIWR, MILWR, LLYSIEUWR, BUGAIL];
export const BACKGROUND_KIT = new Map(BACKGROUNDS.map((one) => [one.id, one]));
/** The background for an id, or `null` for one this content revision does not have. */
export function background(id) {
    return BACKGROUND_KIT.get(id) ?? null;
}
//# sourceMappingURL=backgrounds.js.map